import { useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconButton, VolumeSlider } from '../ui';
import {
  readPlayerPrefs,
  volumeAtLevel,
  writeVolumePrefs,
  type VolumePrefs,
} from '../platform/playerPrefs';
import { MiniNoticeLine } from '../screens/watch/MiniNoticeLine';
import { MuteIcon, PlayIcon } from '../screens/watch/PlayerChrome';
import { useMiniSwipe } from '../screens/watch/miniSwipe';
import { useNarrowViewport } from '../screens/watch/useWatchScreen';
import { queueEndedNotice } from '../queue/model';
import { useCopy } from '../strings/useCopy';
import { createPlayerDock } from './dock';
import styles from './queueEnded.module.css';

/**
 * The corner once the queue has played its list out (2026-08-08) — the minimized player's own box,
 * with a black stage and a sentence where the picture would be.
 *
 * **The corner player is the queue's window, and it stays up as long as the queue does.** At the end
 * of the list the cursor goes to `null` and there is nothing left to play, so the box used to simply
 * vanish: the list was still in the tab, still whole, and the one thing on screen that said so had
 * disappeared at the exact moment the reader was told nothing. It stays instead and says the
 * list is over. The same state arrives from the other side too — removing the playing **last** row
 * leaves entries behind with no cursor — and both reach here, because this box is drawn from the
 * state rather than from the edit that produced it.
 *
 * **It wears the player's shape, down to the strip** (2026-08-08). It cannot *be* `PlayerSurface`:
 * there is no video, no session and no `WatchBinding` to render one from. So it borrows every rule
 * instead — the frame (`createPlayerDock`), the stage and the tile (`.player.mini` and `.video`, via
 * `queueEnded.module.css`), the strip and the ✕'s corner (`miniChrome.module.css`), the place a
 * corner player puts the reason it has no picture (`.miniError`), and the swipe. A box that reshaped
 * itself into a line at the end of a list moved the corner at the one moment the reader was reading
 * it; this one keeps the rectangle and goes black, which is what a player with nothing to play looks
 * like.
 *
 * The strip is therefore the player's six places, and four of them act. **The step back** puts the
 * cursor on the last row and plays it, which is the way back into the list without leaving the page.
 * **Maximize** opens that same row's watch page, and so does an upward drag, so a box a reader puts
 * away by pulling it down is one they open by pushing it up. **The volume pair** moves the stored level
 * (`platform/playerPrefs.ts`): nothing is playing to be heard, and the level is what the next video
 * starts at, so the control still decides something. **The ✕** clears the queue, exactly as it does on
 * the player (`PlaybackHost.tsx`'s `release`): one box, one press, one meaning.
 *
 * Play and the step forward are **drawn and disabled** — there is nothing to play, and nothing after
 * the end of a list — because a strip that dropped two of its six controls would be a different widget
 * in the corner rather than the player with an empty screen.
 */
export function QueueEndedDock({
  onClose,
  onStepBack,
  onMaximize,
}: {
  readonly onClose: () => void;
  /** Back into the list: the cursor goes to the last row and that row plays. */
  readonly onStepBack: () => void;
  /** That same row's watch page. It is the button *and* the upward swipe — see `useMiniSwipe` below. */
  readonly onMaximize: () => void;
}) {
  const copy = useCopy().text;
  const words = copy.queue.ended;
  const narrow = useNarrowViewport();

  /*
   * The stored pair, held here because there is no player to ask and nothing else can move it while
   * this box is up. A video applies it before `load()` is called at all (`useWatchScreen`), so a press
   * on the mute button here is what the reader hears when they go back into the list.
   */
  const [sound, setSound] = useState<VolumePrefs>(() => {
    const stored = readPlayerPrefs();
    return { volume: stored.volume, muted: stored.muted };
  });
  const store = (next: VolumePrefs): void => {
    setSound(next);
    writeVolumePrefs(next);
  };
  // Muted reads as zero on the slider, `MiniControls`' own rule: the level is remembered and the bar
  // states what is audible.
  const level = sound.muted ? 0 : sound.volume;
  /*
   * The same double read `MiniControls` makes, for the same reason: below `md` these buttons sit on
   * `surface-raised`, which is `#ffffff` in the light theme, so `media`'s fixed light ink would be
   * white on white — and a media query cannot change a component variant, so the boundary is read
   * from the token as well as from the stylesheet (docs/specs/web-ui.md).
   */
  const variant = narrow ? 'ghost' : 'media';

  /*
   * The factory builds detached elements and touches no document — `dock.ts` spells out why that is
   * a rule and not a preference, and this is the second dock in the app, i.e. exactly the place a
   * StrictMode double-invocation would orphan a frame in the body.
   */
  const dock = useMemo(() => createPlayerDock(document), []);

  useLayoutEffect(() => {
    dock.attach(document.body);
    return () => dock.detach();
  }, [dock]);

  /*
   * This box lives in the corner and nowhere else — the watch page it opens gets a player of its own,
   * and this one is gone by then. It is still set through the dock rather than assumed, because the
   * frame's resting state is the parking bay and only `setMinimized` gives it the shape a reader can
   * see.
   */
  useLayoutEffect(() => {
    dock.setMinimized(true);
  }, [dock]);

  /*
   * The player's gesture, whole. Up opens the last row's watch page, which is the same act as the
   * Maximize button and is why core is told the box lifts (`miniSwipe.ts`): the box grows under the
   * finger and the release navigates. Down dismisses in both shapes and a push to the right dismisses
   * the popover, and either one clears the queue exactly as the ✕ does. It rides the whole box rather
   * than the strip, so the sentence and the tile are draggable too.
   */
  const swipe = useMiniSwipe({
    sideways: !narrow,
    onClose,
    onMaximize,
    follow: dock.follow,
  });

  return createPortal(
    /*
     * `role="status"` rather than `alert`: the queue ending is the list doing what it was built to
     * do, so it is worth saying and not worth interrupting anybody for. It is also what speaks the
     * sentence below `md`, where the box draws a glyph in its place (`MiniNoticeLine`).
     */
    <div className={styles.box} role="status" {...swipe}>
      {/* Where the picture would be, and black for the same reason the player's letterbox is. */}
      <div className={styles.picture} />

      <div className={styles.chrome}>
        <div className={styles.controls}>
          <IconButton variant={variant} onClick={onStepBack} label={copy.watch.mini.previous}>
            <StepIcon direction="back" />
          </IconButton>

          <IconButton variant={variant} disabled label={copy.watch.chrome.play}>
            <PlayIcon playing={false} />
          </IconButton>

          <IconButton variant={variant} disabled label={copy.watch.mini.next}>
            <StepIcon direction="forward" />
          </IconButton>

          {/* The player's own pair, in the player's own place: the toggle with the slider beside it,
              collapsed until the group is hovered and gone below `md`, where a bar has no room for it
              next to a tile and the toggle answers the only question a bar can ask. */}
          <div className={styles.volumeGroup}>
            <IconButton
              variant={variant}
              onClick={() => store({ volume: sound.volume, muted: !sound.muted })}
              label={sound.muted ? copy.watch.chrome.unmute : copy.watch.chrome.mute}
            >
              <MuteIcon muted={sound.muted} volume={sound.volume} />
            </IconButton>
            <VolumeSlider
              className={styles.volume}
              level={level}
              onChange={(next) => store(volumeAtLevel(sound, next))}
              label={copy.watch.chrome.volume}
            />
          </div>

          {/* Takes the slack, in both shapes: Maximize sits at the far end of the strip. */}
          <span className={styles.spacer} />

          <IconButton variant={variant} onClick={onMaximize} label={copy.watch.mini.maximize}>
            <MaximizeIcon />
          </IconButton>
        </div>

        {/*
         * Outside the strip and after it, the player's arrangement: the top-right corner on the
         * popover, the item after the strip on the bar. It is the one press here that pressing again
         * does not undo, and its name says what it does to the *list* rather than to the box.
         */}
        <IconButton
          className={styles.close}
          variant={variant}
          onClick={onClose}
          label={words.close}
        >
          <CloseIcon />
        </IconButton>

        {/* The player's line, with nothing on it: no second of this box has been played. */}
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressFill} />
        </div>
      </div>

      {/*
       * The sentence, where a corner player states why it has no picture: across the middle of the
       * stage at `md` and up, a glyph on the tile below it. After the strip in source order, so it is
       * drawn over the scrim rather than under it.
       */}
      <div className={styles.notice}>
        <MiniNoticeLine notice={queueEndedNotice()} />
      </div>
    </div>,
    dock.host,
  );
}

/*
 * The four glyphs this box draws, redrawn here rather than shared — this layer's rule for a glyph one
 * component draws, and the same call `MiniControls` and `PlaylistPanel` make about the step triangle
 * they both wear. `PlayIcon` and `MuteIcon` are the exception because they are already exported for
 * the two chromes that share them.
 */

/** A step through the list: the transport triangle against a bar. */
function StepIcon({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g transform={direction === 'forward' ? undefined : 'translate(24 0) scale(-1 1)'}>
        <path fill="currentColor" d="M6 5.5 15.5 12 6 18.5z" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          d="M17.8 5.5v13"
        />
      </g>
    </svg>
  );
}

/** The fullscreen glyph's outward arrows: this restores a window rather than filling the screen. */
function MaximizeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7"
      />
    </svg>
  );
}

/** The player's own ✕. */
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        d="M6 6l12 12M18 6 6 18"
      />
    </svg>
  );
}
